/**
 * Money, rendered with the account's currency.
 *
 * ⛔ FOUND BY READING A SCREENSHOT, NOT BY AN ASSERTION. The contacts list
 * showed a column headed "Value" whose cells read `3900.00`. Nothing was broken
 * and nothing failed; the number simply had no unit, and a reader cannot tell
 * 3,900 euros from 3,900 baht.
 *
 * ⛔ THE STRING IS PASSED TO `Intl` AS A STRING, NEVER THROUGH `Number()`.
 * `numeric` reaches the client as text precisely so nothing rounds it, and
 * `Number('3900.00')` throws that away to save one character. `format` has
 * accepted a string since ES2023 and formats the decimal exactly - this project
 * already lost the scale off a money value once, in `jsonb_build_object`, and
 * that is the same mistake one layer up.
 */
/**
 * ⛔ THE DECIMAL PLACES COME FROM THE ACCOUNT, AND THEY ARE A PARAMETER RATHER
 * THAN A DEFAULT HERE. Amir 2026-09-12: decimal places are an admin setting,
 * zero for this deployment. `org_settings.value_decimal_places` is read by
 * `orgLabels()` and travels beside the currency, because one call applies both.
 *
 * ⚠️ DISPLAY ONLY. `value_total` keeps its full `numeric` scale, so an account
 * that later wants two places has not lost the cents - the same distinction
 * `timestamptz` storage draws against a display timezone.
 *
 * ⚠️ AND IT IS OPTIONAL, so the two existing callers that have not been given a
 * value yet keep `Intl`'s currency default rather than silently becoming zero.
 * A missing setting must not look like a chosen one.
 */
export function formatMoney(amount: string, currency: string, decimals?: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      ...(decimals === undefined
        ? {}
        : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
    }).format(amount as unknown as number)
  } catch {
    /**
     * ⚠️ AN UNKNOWN CURRENCY CODE FALLS BACK TO CODE-PLUS-NUMBER RATHER THAN TO
     * THE BARE NUMBER. `Intl` throws on a code it does not know, and dropping
     * the unit on the way past would silently restore the exact defect this
     * file exists to fix.
     */
    return `${currency} ${amount}`
  }
}
